// Net working time as the timesheet forms see it, shared by the controllers that
// mirror a work-time rule live (work_time_limit_controller, break_rule_controller).
//
// Kept in one place because the parsing is full of form-specific traps: a stepper's
// hidden field and its visible display input share a name, multi_entry_controller
// clones whole entry blocks under `additional_entries[N][...]`, and 00:00 as an end
// time means end-of-day rather than midnight (matching ConfigurableTimeSheet).

// Minutes since midnight for an "HH:MM" string; 0 for anything unparseable.
export function toMinutes(value) {
  if (!value) return 0
  const parts = String(value).split(":")
  if (parts.length < 2) return 0
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)
}

export function minutesToHHMM(minutes) {
  const hours = String(Math.floor(minutes / 60)).padStart(2, "0")
  return `${hours}:${String(minutes % 60).padStart(2, "0")}`
}

// Net minutes of one entry block. Returns 0 while the window is incomplete.
export function entryNetMinutes({ time_from: from, time_to: to, time_break: brk }) {
  if (!from || !to) return 0

  const fromMinutes = toMinutes(from)
  let toMinutes_ = toMinutes(to)
  // 00:00 as an end time means end-of-day, matching ConfigurableTimeSheet.
  if (toMinutes_ === 0 && fromMinutes > 0) toMinutes_ = 1440
  if (toMinutes_ < fromMinutes) toMinutes_ += 1440 // overnight

  return Math.max(toMinutes_ - fromMinutes - toMinutes(brk), 0)
}

// The name prefixes that ARE timesheet entries: the base entry, the clones
// multi_entry_controller makes, and the Schnellerfassung's rows.
//
// A form holds other collections whose rows carry von/bis of their own —
// `fleet_usages[row_0][time_from]` for the Fahrzeug-Einsatz rows and
// `site_allocations[N][time_from]` for a multi-site day. Matching those read a
// vehicle as if it were a person: the row has no Pause field, so `entered` was
// 0, every row reported the whole statutory break as missing, and the form
// announced "Die Pause wurde auf 00:30 erhöht" on a Pause it never touched
// (writeBreak found no input to write). The allocations meanwhile were summed
// into the day total behind the duration limits, which #formNetMinutes below
// explicitly says must not happen.
const ENTRY_PREFIX = /^(configurable_time_sheet|additional_entries\[[^\]]+\]|entries\[[^\]]+\])$/

// Every entry block in the form as { prefix, element, values }: the base
// `configurable_time_sheet[...]` block plus each `additional_entries[N][...]` clone
// multi_entry_controller made.
//
// `element` is the entry's own container, which matters for anything evaluated PER
// entry — break-period rows and hint texts both live inside it, and multi_entry
// clones them, so a form-wide querySelector would judge every entry by the first
// one's markup. Falls back to `root` on a form with no multi-entry wrapper.
//
// The Schnellerfassung builds its rows itself (quick_entries_controller) rather
// than through multi_entry, so its row container is named here too — otherwise
// every quick row resolves to the form and they all share one row's hints.
export function entryBlocks(root) {
  const blocks = new Map()

  root.querySelectorAll("input[name]").forEach((input) => {
    const match = input.name.match(/^(.*)\[(time_from|time_to|time_break)\]$/)
    if (!match) return

    const [, prefix, field] = match
    if (!ENTRY_PREFIX.test(prefix)) return
    if (!blocks.has(prefix)) {
      blocks.set(prefix, {
        prefix,
        element:
          input.closest('[data-multi-entry-target="entry"], [data-quick-entries-target="entry"]') ||
          root,
        values: {},
      })
    }
    const { values } = blocks.get(prefix)
    // First non-empty wins: a stepper's hidden field and its visible display
    // input share the name, and hidden fields come first in the markup.
    if (!values[field] && input.value) values[field] = input.value
  })

  return [...blocks.values()]
}

// The time fields of every entry block, keyed by name prefix. Kept for callers
// that only need the numbers.
export function entryTimeGroups(root) {
  return new Map(entryBlocks(root).map(({ prefix, values }) => [prefix, values]))
}

// Net minutes across every entry block — the day total the server-side rules are
// evaluated on. Multi-site allocations carve up the base window rather than adding
// to it, so they are deliberately not summed.
export function formNetMinutes(root) {
  let total = 0
  entryTimeGroups(root).forEach((group) => {
    total += entryNetMinutes(group)
  })
  return total
}
